'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { VideoForm, type VideoFormRef } from '@/components/partials/videos'
import { useUpdateVideo, useVideo } from '@/hooks'
import { type CreateVideoParams } from '@/types/video'
import { FormModeEnum } from '@/enums'
import { ContentError, FormSkeleton } from '@/components/partials'

type PropsType = {
  id: string
}

const Update = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const [isRedirecting, setIsRedirecting] = useState(false)
  const videoFormRef = useRef<VideoFormRef>(null)

  const { data: video, isLoading, error } = useVideo(id)
  const { mutate: updateVideo, isPending } = useUpdateVideo()

  const handleSubmit = (data: CreateVideoParams) => {
    updateVideo(
      { id, params: data },
      {
        onSuccess: async updatedVideo => {
          setIsRedirecting(true)
          queryClient.setQueryData(['video', id], updatedVideo)
          await queryClient.invalidateQueries({ queryKey: ['videos'] })
          toast.success(t('updated_successfully'))
          router.push(`/galleries/videos/${updatedVideo.id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (videoFormRef.current) {
        videoFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_updating'))
  }

  const handleCancel = () => {
    router.push(`/galleries/videos/${id}`)
  }

  if (isLoading || !video) {
    return <FormSkeleton fieldRows={1} showTranslations />
  }

  if (error || !video) {
    return <ContentError error={error} listUrl="/galleries/videos" />
  }

  return (
    <VideoForm
      ref={videoFormRef}
      mode={FormModeEnum.UPDATE}
      initialData={video}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
